/**
 * Tesla Vehicle Selector – Static data, dropdown UI
 * No API calls – all data is hardcoded for Tesla lineup
 *
 * Models:
 *   - Model 3 Highland (2023–2025): SR RWD, LR RWD, LR AWD, Performance
 *   - Model Y Juniper  (2025–2026): RWD, LR AWD, Performance (5 or 7 seat)
 *
 * Performance trim → Sport Seats; all others → Standard Seats
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'teslaVehicleSelection';
  var STORAGE_TTL = 7 * 24 * 60 * 60 * 1000;

  /* ── Tesla Data ── */
  var TESLA_DATA = {
    model3: {
      label: 'Model 3 Highland',
      years: '2023-2025',
      hasSeating: false,
      trims: [
        { value: 'Standard Range RWD', seatType: 'Standard' },
        { value: 'Long Range RWD',     seatType: 'Standard' },
        { value: 'Long Range AWD',     seatType: 'Standard' },
        { value: 'Performance',        seatType: 'Sport' }
      ]
    },
    modelY: {
      label: 'Model Y Juniper',
      years: '2025-2026',
      hasSeating: true,
      seatingOptions: ['5 seats', '7 seats'],
      trims: [
        { value: 'RWD',           seatType: 'Standard' },
        { value: 'Long Range AWD', seatType: 'Standard' },
        { value: 'Performance',    seatType: 'Sport' }
      ]
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function(event) {
    if (event.detail.sectionId) {
      init();
    }
  });

  function init() {
    var container = document.getElementById('tesla-vehicle-selector-block');
    if (!container) return;
    if (container.dataset.initialized === 'true') return;
    container.dataset.initialized = 'true';

    /* ── DOM refs ── */
    var selYear = document.getElementById('tesla-year');
    var selModel = document.getElementById('tesla-model');
    var selTrim = document.getElementById('tesla-trim');
    var selSeat = document.getElementById('tesla-seat');
    var seatWrapper = document.getElementById('tesla-seat-wrapper');
    
    var propSeatType = document.getElementById('tesla-prop-seat-type');

    var validationMsg = document.getElementById('vehicle-validation-message');
    var validationFrame = null;

    /* ── Helpers ── */
    function isValidYearForModel(yearStr, yearRange) {
      if (!yearStr) return false;
      var y = parseInt(yearStr, 10);
      var parts = yearRange.split('-');
      var min = parseInt(parts[0], 10);
      var max = parseInt(parts[1], 10);
      return y >= min && y <= max;
    }

    function clearSelect(selectElem, defaultText) {
      selectElem.innerHTML = '<option value="">' + defaultText + '</option>';
      selectElem.disabled = true;
    }

    function populateSelect(selectElem, options, defaultText) {
      clearSelect(selectElem, defaultText);
      options.forEach(function(opt) {
        var o = document.createElement('option');
        o.value = opt.value || opt;
        o.textContent = opt.label || opt.value || opt;
        selectElem.appendChild(o);
      });
      selectElem.disabled = false;
    }

    function getModelKeyByLabel(label) {
      for (var key in TESLA_DATA) {
        if (TESLA_DATA[key].label === label) return key;
      }
      return null;
    }

    /* ── Change Handlers ── */
    function handleYearChange() {
      var year = selYear.value;
      clearSelect(selModel, 'Select Model');
      clearSelect(selTrim, 'Select Trim');
      clearSelect(selSeat, 'Select Seat');
      seatWrapper.style.display = 'none';
      updateSeatBadge('');

      if (year) {
        var models = [];
        for (var key in TESLA_DATA) {
          if (isValidYearForModel(year, TESLA_DATA[key].years)) {
            models.push({ value: TESLA_DATA[key].label, label: TESLA_DATA[key].label });
          }
        }
        if (models.length > 0) {
          populateSelect(selModel, models, 'Select Model');
        }
      }
      validateAndSyncSteps();
    }

    function handleModelChange() {
      var modelLabel = selModel.value;
      clearSelect(selTrim, 'Select Trim');
      clearSelect(selSeat, 'Select Seat');
      seatWrapper.style.display = 'none';
      updateSeatBadge('');

      if (modelLabel) {
        var modelKey = getModelKeyByLabel(modelLabel);
        var data = TESLA_DATA[modelKey];
        if (data) {
          var trims = data.trims.map(function(t) { return { value: t.value, label: t.value }; });
          populateSelect(selTrim, trims, 'Select Trim');
        }
      }
      validateAndSyncSteps();
    }

    function handleTrimChange() {
      var modelLabel = selModel.value;
      var trimValue = selTrim.value;
      clearSelect(selSeat, 'Select Seat');
      seatWrapper.style.display = 'none';
      updateSeatBadge('');

      if (modelLabel && trimValue) {
        var modelKey = getModelKeyByLabel(modelLabel);
        var data = TESLA_DATA[modelKey];
        if (data) {
          var seatType = '';
          for (var i = 0; i < data.trims.length; i++) {
            if (data.trims[i].value === trimValue) {
              seatType = data.trims[i].seatType;
              break;
            }
          }
          updateSeatBadge(seatType);

          if (data.hasSeating && selYear.value === '2026') {
            seatWrapper.style.display = '';
            var seats = data.seatingOptions.map(function(s) { return { value: s, label: s }; });
            populateSelect(selSeat, seats, 'Select Seat');
            selSeat.required = true;
          } else {
            selSeat.required = false;
            selSeat.disabled = true;
          }
        }
      }
      validateAndSyncSteps();
    }

    function updateSeatBadge(seatType) {
      if (propSeatType) propSeatType.value = seatType;
    }

    /* ── Validation & Stepper ── */
    function isConfigurationComplete() {
      var year = selYear.value;
      var modelLabel = selModel.value;
      var trim = selTrim.value;
      
      var vehicleDone = !!(year && modelLabel && trim);
      
      if (vehicleDone) {
        var modelKey = getModelKeyByLabel(modelLabel);
        var data = TESLA_DATA[modelKey];
        if (data && data.hasSeating && selYear.value === '2026') {
          vehicleDone = vehicleDone && !!selSeat.value;
        }
      }

      var colorPicker = document.querySelector('.product-color-picker');
      var setupPicker = document.querySelector('.product-setup-picker');
      var colorSelected = colorPicker ? colorPicker.querySelector('input:checked') : true;
      var setupSelected = setupPicker ? setupPicker.querySelector('input:checked') : true;

      return vehicleDone && colorSelected && setupSelected;
    }

    function runValidateAndSyncSteps() {
      var isComplete = isConfigurationComplete();

      var year = selYear.value;
      var modelLabel = selModel.value;
      var trim = selTrim.value;
      
      var vehicleDone = !!(year && modelLabel && trim);
      if (vehicleDone) {
        var modelKey = getModelKeyByLabel(modelLabel);
        var data = TESLA_DATA[modelKey];
        if (data && data.hasSeating && selYear.value === '2026') {
          vehicleDone = vehicleDone && !!selSeat.value;
        }
      }

      var colorPicker = document.querySelector('.product-color-picker');
      var setupPicker = document.querySelector('.product-setup-picker');
      var colorSelected = colorPicker ? colorPicker.querySelector('input:checked') : true;

      var step1 = document.getElementById('step-1-container');
      var step2 = document.getElementById('step-2-container');
      var step3 = document.getElementById('step-3-container');

      if (vehicleDone) {
        if (step1) step1.classList.add('completed');
        if (step2) step2.classList.add('active');
        if (validationMsg) validationMsg.style.display = 'none';
      } else {
        if (step1) step1.classList.remove('completed');
        if (step2) step2.classList.remove('active');
        if (step3) step3.classList.remove('active');
        if (validationMsg && year) validationMsg.style.display = 'block';
      }

      if (vehicleDone && colorSelected) {
        if (step2) step2.classList.add('completed');
        if (step3) step3.classList.add('active');
      } else {
        if (step2) step2.classList.remove('completed');
        if (step3) step3.classList.remove('active');
      }

      if (isComplete) {
        if (step3) step3.classList.add('completed');
      } else {
        if (step3) step3.classList.remove('completed');
      }

      /* ATC button gating */
      var atcButtons = document.querySelectorAll('.m-add-to-cart');
      Array.prototype.forEach.call(atcButtons, function (btn) {
        if (isComplete) {
          btn.setAttribute('data-steps-complete', 'true');
          btn.style.opacity = '1';
        } else {
          btn.setAttribute('data-steps-complete', 'false');
          btn.style.opacity = '0.7';
        }
      });

      if (vehicleDone) { saveSelection(); } else { clearStorage(); }
    }

    function validateAndSyncSteps() {
      if (validationFrame) return;
      validationFrame = requestAnimationFrame(function () {
        validationFrame = null;
        runValidateAndSyncSteps();
      });
    }

    /* ── Storage: LocalStorage + 7-day TTL ── */
    function saveSelection() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          year: selYear.value,
          model: selModel.value,
          trim: selTrim.value,
          seating: selSeat.value,
          ts: Date.now()
        }));
      } catch (e) { }
    }

    function loadSelection() {
      try {
        var raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        var data = JSON.parse(raw);
        if (Date.now() - data.ts > STORAGE_TTL) {
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }
        return data;
      } catch (e) { return null; }
    }

    function clearStorage() {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    }

    function restoreSelection() {
      var saved = loadSelection();
      if (!saved || !saved.year) return;
      
      selYear.value = saved.year;
      handleYearChange();
      
      if (saved.model && !selModel.disabled) {
        selModel.value = saved.model;
        handleModelChange();
        
        if (saved.trim && !selTrim.disabled) {
          selTrim.value = saved.trim;
          handleTrimChange();
          
          if (saved.seating && !selSeat.disabled) {
            selSeat.value = saved.seating;
          }
        }
      }
      
      validateAndSyncSteps();
    }

    /* ── Events ── */
    selYear.addEventListener('change', handleYearChange);
    selModel.addEventListener('change', handleModelChange);
    selTrim.addEventListener('change', handleTrimChange);
    selSeat.addEventListener('change', validateAndSyncSteps);

    document.addEventListener('change', function (e) {
      if (e.target.closest('.product-color-picker') || e.target.closest('.product-setup-picker')) {
        validateAndSyncSteps();
      }
    });

    document.addEventListener('click', function (e) {
      var atcBtn = e.target.closest('.m-add-to-cart');
      if (atcBtn) {
        var isComplete = atcBtn.getAttribute('data-steps-complete') === 'true' || isConfigurationComplete();
        if (!isComplete) {
          var stepper = document.querySelector('.vehicle-config-stepper');
          if (stepper) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            var offset = 120;
            var elementPosition = stepper.getBoundingClientRect().top;
            var offsetPosition = elementPosition + window.pageYOffset - offset;
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
          }
        }
      }
    }, true);

    /* ── Init ── */
    restoreSelection();
    setTimeout(function () { validateAndSyncSteps(); }, 500);

    /* Listen for variant changes from theme */
    if (window.MinimogEvents) {
      MinimogEvents.subscribe(MinimogTheme.pubSubEvents.variantChange, function () {
        validateAndSyncSteps();
      });
    } else {
      document.addEventListener('variant:changed', function () {
        validateAndSyncSteps();
      });
    }
  }
})();
